/**
 * DateSection Component
 * Collapsible section for admin users to set a backdated check-in date
 */

import { TextInput } from '@/components/ui/text-input';
import Ionicons from '@expo/vector-icons/Ionicons';
import React, { useCallback, useRef, useState } from 'react';
import { Platform, Pressable, Text, View } from 'react-native';

export interface DateSectionProps {
  /** ISO datetime string or null (null means "now") */
  value: string | null;
  /** Callback when date changes */
  onChange: (date: string | null) => void;
  /** Whether the section is disabled */
  disabled?: boolean;
}

/**
 * Format an ISO datetime string for display.
 */
function formatDisplayDate(iso: string): string {
  try {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return iso;
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Convert an ISO datetime string to the `datetime-local` input format (YYYY-MM-DDTHH:mm).
 */
function toDatetimeLocalValue(iso: string | null): string {
  if (!iso) return '';
  try {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${d}T${h}:${min}`;
  } catch {
    return '';
  }
}

/**
 * Validate a YYYY-MM-DD string and convert to ISO datetime.
 */
function parseDateString(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  // Accept YYYY-MM-DD or YYYY-MM-DDTHH:mm
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/;
  const dateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
  if (!dateOnly.test(trimmed) && !dateTime.test(trimmed)) return null;
  const date = new Date(dateOnly.test(trimmed) ? `${trimmed}T12:00:00` : trimmed);
  if (isNaN(date.getTime())) return null;
  return date.toISOString();
}

export function DateSection({ value, onChange, disabled = false }: DateSectionProps) {
  const [isExpanded, setIsExpanded] = useState(value !== null);
  const [nativeInput, setNativeInput] = useState('');
  const [nativeError, setNativeError] = useState<string | null>(null);
  const [webInputFocused, setWebInputFocused] = useState(false);
  const webInputRef = useRef<HTMLInputElement | null>(null);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleClear = useCallback(() => {
    onChange(null);
    setNativeInput('');
    setNativeError(null);
  }, [onChange]);

  const handleWebChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (!val) {
        onChange(null);
        return;
      }
      // datetime-local gives us YYYY-MM-DDTHH:mm
      const date = new Date(val);
      if (!isNaN(date.getTime())) {
        onChange(date.toISOString());
      }
    },
    [onChange]
  );

  const handleNativeBlur = useCallback(() => {
    if (!nativeInput.trim()) {
      setNativeError(null);
      return;
    }
    const parsed = parseDateString(nativeInput);
    if (parsed) {
      onChange(parsed);
      setNativeError(null);
    } else {
      setNativeError('Use YYYY-MM-DD format');
    }
  }, [nativeInput, onChange]);

  return (
    <View className="mb-4">
      {/* Header Row */}
      <Pressable
        onPress={toggleExpanded}
        className="flex-row items-center justify-between py-3"
        disabled={disabled}
      >
        <View className="flex-row items-center">
          <Ionicons name="calendar-outline" size={20} className="text-foreground" />
          <Text className="text-base font-medium ml-2 text-foreground">
            Set Date
          </Text>
          {value !== null && (
            <View className="ml-2 px-2 py-0.5 rounded-full bg-primary/[0.13]">
              <Ionicons name="checkmark" size={12} className="text-primary" />
            </View>
          )}
        </View>
        <View className="flex-row items-center">
          <View className="mr-2 px-2 py-0.5 rounded-full bg-warning/[0.13]">
            <Text className="text-xs font-medium text-warning">
              Admin
            </Text>
          </View>
          <Ionicons
            name={isExpanded ? 'chevron-up' : 'chevron-down'}
            size={18}
            className="text-muted-foreground"
          />
        </View>
      </Pressable>

      {/* Expanded Content */}
      {isExpanded && (
        <View className="mt-2">
          {Platform.OS === 'web' ? (
            // Web: native datetime-local input
            <View
              className={`p-3 rounded-lg bg-surface border ${webInputFocused ? 'border-primary/60' : 'border-border'}`}
            >
              <input
                ref={webInputRef}
                type="datetime-local"
                value={toDatetimeLocalValue(value)}
                onChange={handleWebChange as any}
                onFocus={() => setWebInputFocused(true)}
                onBlur={() => setWebInputFocused(false)}
                disabled={disabled}
                style={{
                  backgroundColor: 'transparent',
                  color: 'inherit',
                  border: 'none',
                  outline: 'none',
                  fontSize: 16,
                  fontFamily: 'inherit',
                  width: '100%',
                }}
                className="text-foreground"
              />
            </View>
          ) : (
            // Native: plain text input with format validation
            <TextInput
              value={nativeInput}
              onChangeText={setNativeInput}
              onBlur={handleNativeBlur}
              placeholder="YYYY-MM-DD"
              editable={!disabled}
              keyboardType="numbers-and-punctuation"
              autoCapitalize="none"
              autoCorrect={false}
              error={nativeError || undefined}
              accessibilityLabel="Check-in date input"
              accessibilityHint="Enter date in YYYY-MM-DD format"
            />
          )}

          {/* Selected date display + clear button */}
          {value !== null && (
            <View className="flex-row items-center justify-between mt-2">
              <Text className="text-sm text-muted-foreground">
                {formatDisplayDate(value)}
              </Text>
              <Pressable
                onPress={handleClear}
                disabled={disabled}
                className="px-3 py-1 rounded-full bg-error/[0.09]"
              >
                <Text className="text-sm font-medium text-error">
                  Clear
                </Text>
              </Pressable>
            </View>
          )}
        </View>
      )}
    </View>
  );
}

export default DateSection;
